/**
 * The circular section in the terms the shared plotting code uses: heights
 * are measured above the invert — the circle's lowest point — so the SPL is at
 * R and the crown at 2R.
 */

const TOLERANCE = 1e-9;
const round2 = (v) => Math.round(v * 100) / 100;

/**
 * Range of heights at offset d that lie on the tunnel face: between the lower
 * and upper arcs of the circle. Null when d is wider than the section.
 */
export function circularHeightRange(d, R) {
  const x = Math.abs(Number(d));
  const radius = Number(R);
  if (!isFinite(x) || !(radius > 0) || x > radius + TOLERANCE) return null;
  const half = Math.sqrt(Math.max(0, radius * radius - x * x));
  return { min: radius - half, max: radius + half };
}

/**
 * The shared orientation sources read the section as width at SPL, height and
 * wall height. For a circle those are 2R, 2R and R: the known-chainages
 * source then takes 2R across the SPL and R up to the crown, exactly what the
 * circular trace routine uses.
 */
const SECTION_KEYS = ["width", "widthspl", "height", "hw"];
export function withCircularSection(dim) {
  const R = Number(dim.radiusofcrown);
  return { ...dim, width: 2 * R, widthspl: 2 * R, height: 2 * R, hw: R };
}
export function withoutCircularSection(dim) {
  const next = { ...dim };
  SECTION_KEYS.forEach((key) => delete next[key]);
  return next;
}

/**
 * Traces saved before the face-point form gave "Any Other Point" as a height
 * above, below or at the SPL plane. It is now the height above the invert, as
 * in the D-shape and horseshoe. Rewrites one saved trace so it plots where it
 * did. `geometry` is the first trace, which fixes the radius for the whole log.
 * Pure: returns a new object, and leaves traces without the old field alone.
 */
export function migrateLegacyFacePoint(dim, geometry = dim) {
  if (!dim || dim.locationwrtSPL === undefined) return dim;
  const { locationwrtSPL, ...next } = dim;
  if (next.orientation !== "otherpoint" || !locationwrtSPL) return next;

  const R = Number(geometry.radiusofcrown) || 0;
  const oldHp = Number(dim.verticalheight) || 0;
  const Hp = locationwrtSPL === "above" ? R + oldHp : locationwrtSPL === "below" ? R - oldHp : R;
  return { ...next, verticalheight: round2(Math.max(0, Math.min(2 * R, Hp))) };
}
